import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        // Check localStorage first, then system preference
        try {
            const darkModeEnabled = localStorage.getItem('darkModeEnabled');
            if (darkModeEnabled !== null) {
                return darkModeEnabled === 'true';
            }
        } catch (error) {
            console.warn('Failed to read theme from localStorage:', error);
        }
        // If no saved preference or error, check system preference
        try {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        } catch (error) {
            console.warn('Failed to check system theme preference:', error);
            return false; // Default to light mode
        }
    });

    // Initialize and sync the dark class on <html> element
    useEffect(() => {
        const root = document.documentElement; // The <html> element
        
        // Step 1: Temporarily disable ALL transitions
        root.classList.add('theme-transitioning');
        
        // Step 2: Change the theme class
        if (isDarkMode) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        
        // Step 3: Re-enable transitions on next frame (after colors have changed)
        setTimeout(() => root.classList.remove('theme-transitioning'), 0);
    }, [isDarkMode]);

    const toggleTheme = () => {
        setIsDarkMode(prev => {
            const newVal = !prev;
            try {
                localStorage.setItem('darkModeEnabled', String(newVal));
            } catch (error) {
                console.warn('Failed to save theme to localStorage:', error);
            }
            return newVal;
        });
    };

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};